/**
 * Element properties the Set as Graphic Standard modal can change directly in
 * the template source (PLAT-11993), for small adjustments without opening
 * DatoCMS. They are Creatomate element properties, not controls: the editor
 * shows no field for them, so the template source is where they live.
 *
 * - Text: horizontal / vertical alignment, line height, letter spacing.
 * - Image (logo, headshot, background): fit, opacity, colour overlay.
 *
 * No text opacity: the renderer drops it on text, which is why templates use
 * an rgba alpha in the colour instead.
 *
 * Pure: a catalog, a reader, and the merge applied on save.
 */

import type { Source } from '@src/components/sidepanel/CompositionSettings/types';

export type SourceFieldKind = 'select' | 'percent' | 'color';

export interface SourceFieldOption {
  value: string;
  label: string;
}

export interface SourceField {
  property: string;
  label: string;
  kind: SourceFieldKind;
  options?: SourceFieldOption[];
  /** Stepper increment for `percent` fields. */
  step?: number;
  /** Where a `percent` stepper starts when the template leaves it unset —
   *  the renderer's own default for the property. */
  startFrom?: number;
}

const HORIZONTAL: SourceFieldOption[] = [
  { value: '0%', label: 'Left' },
  { value: '50%', label: 'Center' },
  { value: '100%', label: 'Right' },
];
const VERTICAL: SourceFieldOption[] = [
  { value: '0%', label: 'Top' },
  { value: '50%', label: 'Middle' },
  { value: '100%', label: 'Bottom' },
];
const FIT: SourceFieldOption[] = [
  { value: 'cover', label: 'Cover' },
  { value: 'contain', label: 'Contain' },
  { value: 'fill', label: 'Fill' },
];

const TEXT_FIELDS: SourceField[] = [
  {
    property: 'x_alignment',
    label: 'Horizontal Alignment',
    kind: 'select',
    options: HORIZONTAL,
  },
  {
    property: 'y_alignment',
    label: 'Vertical Alignment',
    kind: 'select',
    options: VERTICAL,
  },
  {
    property: 'line_height',
    label: 'Line Height',
    kind: 'percent',
    step: 5,
    startFrom: 100,
  },
  {
    property: 'letter_spacing',
    label: 'Letter Spacing',
    kind: 'percent',
    step: 1,
    startFrom: 0,
  },
];

const IMAGE_FIELDS: SourceField[] = [
  { property: 'fit', label: 'Fit', kind: 'select', options: FIT },
  {
    property: 'opacity',
    label: 'Opacity',
    kind: 'percent',
    step: 5,
    startFrom: 100,
  },
  { property: 'color_overlay', label: 'Color Overlay', kind: 'color' },
];

/** The extra fields an element of this Creatomate type offers. */
export function sourceFieldsFor(elementType: unknown): SourceField[] {
  if (elementType === 'text') return TEXT_FIELDS;
  if (elementType === 'image') return IMAGE_FIELDS;
  return [];
}

type Node = Record<string, unknown>;

const isNode = (v: unknown): v is Node => !!v && typeof v === 'object';

/** The element at an index path from the source root, if any. */
export function elementAtPath(
  source: Pick<Source, 'elements'> | null | undefined,
  path: number[],
): Node | undefined {
  let node: unknown = source;
  for (const index of path) {
    if (!isNode(node)) return undefined;
    const children = node.elements;
    if (!Array.isArray(children)) return undefined;
    node = children[index];
  }
  return isNode(node) ? node : undefined;
}

export interface SourcePropertyOverride {
  /** Index path from the template root, as `elementPaths` address elements. */
  path: number[];
  property: string;
  /** The new value; `''` removes the property so the renderer's default applies. */
  value: string;
}

/**
 * The source with each override written onto its element. Returns the input
 * itself when there is nothing to write, otherwise a deep clone.
 */
export function applySourcePropertyOverrides<
  T extends Pick<Source, 'elements'>,
>(source: T, overrides: SourcePropertyOverride[]): T {
  if (overrides.length === 0) return source;
  const clone: T = JSON.parse(JSON.stringify(source));
  overrides.forEach(({ path, property, value }) => {
    const el = elementAtPath(clone, path);
    if (!el) return;
    if (value === '') delete el[property];
    else el[property] = value;
  });
  return clone;
}
